import React, { useState, useEffect } from "react";
import api from "../../../services/api";
import PageLayout from "../../../components/layout/PageLayout";
import { Header, DateRangePicker } from "../../../components/ui";
import {
  FaTrashAlt,
  FaRoute,
  FaTruck,
  FaUser,
  FaMapMarkerAlt,
} from "react-icons/fa";
import styles from "./RemoveRoutes.module.css";

const todayStr = new Date().toISOString().split("T")[0];

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const getProgressWidths = (route) => {
  const total = route.receipts_count || 0;
  const delivered = route.delivered_count || 0;
  const inProgress = route.in_progress_count || 0;
  const undelivered = route.undelivered_count || 0;

  if (!total) {
    return { delivered: 0, inProgress: 0, undelivered: 0, total };
  }

  return {
    delivered: (delivered / total) * 100,
    inProgress: (inProgress / total) * 100,
    undelivered: (undelivered / total) * 100,
    total,
    deliveredCount: delivered,
    undeliveredCount: undelivered,
  };
};

export default function RemoveRoutes() {
  const [routes, setRoutes] = useState([]);
  const [dateRange, setDateRange] = useState({ from: todayStr, to: todayStr });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const loadRoutes = async (range = dateRange) => {
    if (!range?.from || !range?.to) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get("/routes/by-date-lite", {
        params: { startDate: range.from, endDate: range.to },
      });
      setRoutes(data || []);
      // вычистим выбранные, которых больше нет в списке
      setSelectedIds((prev) =>
        prev.filter((id) => (data || []).some((r) => r.id === id))
      );
    } catch (e) {
      console.error("Ошибка загрузки маршрутов", e);
      setError(e.response?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoutes(dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.from, dateRange.to]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (!routes.length) return;
    const pageIds = routes.map((r) => r.id);
    const allSelected = pageIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      // снять выделение только с тех, что на странице
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      // добавить все на странице
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    const ok = window.confirm(
      `Удалить ${selectedIds.length} маршрутов с их квитанциями и товарами?`
    );
    if (!ok) return;

    try {
      await api.delete("/routes/removecascade-bulk", {
        data: { ids: selectedIds },
      });
      setRoutes((prev) => prev.filter((r) => !selectedIds.includes(r.id)));
      setSelectedIds([]);
    } catch (e) {
      console.error("Ошибка bulk-удаления маршрутов", e);
      alert("Не удалось удалить маршруты.");
    }
  };

  const content =
    loading ? (
      <p className={styles.stateText}>Загрузка...</p>
    ) : error ? (
      <p className={styles.stateText}>Ошибка: {error}</p>
    ) : routes.length === 0 ? (
      <p className={styles.stateText}>Маршруты не найдены</p>
    ) : (
      <div className={styles.cardsWrapper}>
        {routes.map((route) => {
          const widths = getProgressWidths(route);
          const isSelected = selectedIds.includes(route.id);

          return (
            <div
              key={route.id}
              className={`${styles.card} ${
                isSelected ? styles.selected : ""
              }`}
              onClick={(e) => {
                // чтобы клик по чекбоксу не дублировался
                if ((e.target).closest?.(`.${styles.checkboxWrap}`)) return;
                toggleSelect(route.id);
              }}
            >
              <div className={styles.topRow}>
                <div className={styles.header}>
                  <h3>
                    <FaRoute /> №{route.route_number}
                  </h3>
                  <span className={styles.dateLabel}>
                    {formatDate(route.route_date)}
                  </span>
                </div>
                <label className={styles.checkboxWrap}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(route.id)}
                  />
                  <span className={styles.checkboxFake} />
                </label>
              </div>

              <div className={styles.info}>
                <p>
                  <FaTruck /> <b>Машина:</b> {route.car || "—"}
                </p>
                <p>
                  <FaUser /> <b>Водитель:</b> {route.driver || "—"}
                </p>
                {route.route_districts && (
                  <p>
                    <FaMapMarkerAlt /> <b>Районы:</b> {route.route_districts}
                  </p>
                )}
                <p>
                  <b>Статус:</b> {route.route_status || "—"}
                </p>
                <p>
                  <b>Квитанций:</b> {route.receipts_count ?? 0}
                </p>
              </div>

              <div className={styles.progress}>
                <div className={styles.bar}>
                  <div
                    className={styles.delivered}
                    style={{ width: `${widths.delivered || 0}%` }}
                  />
                  <div
                    className={styles.inProgress}
                    style={{ width: `${widths.inProgress || 0}%` }}
                  />
                  <div
                    className={styles.undelivered}
                    style={{ width: `${widths.undelivered || 0}%` }}
                  />
                </div>
                <p className={styles.progressText}>
                  {widths.total
                    ? `${widths.deliveredCount}/${widths.total} доставлено, ${
                        widths.undeliveredCount
                      } не доставлено`
                    : "Нет квитанций"}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );

  return (
    <PageLayout
      header={
        <Header
          icon={FaTrashAlt}
          title="Удаление маршрутных листов"
          subtitle="Выберите маршруты за период и удалите их каскадно"
        />
      }
controls={
  <>
    <DateRangePicker
      value={dateRange}
      onChange={(value) => {
        if (value?.from && value?.to) {
          setDateRange(value);
        }
      }}
    />

    <div className={styles.bulkActions}>
      <button
        className={styles.selectAllBtn}
        onClick={toggleSelectAll}
        disabled={!routes.length}
      >
        Выделить все на странице
      </button>
      <button
        className={styles.deleteSelectedBtn}
        onClick={handleBulkDelete}
        disabled={!selectedIds.length}
      >
        <FaTrashAlt />
        Удалить выбранные ({selectedIds.length})
      </button>
    </div>
  </>
}

    >
      {content}
    </PageLayout>
  );
}
